'use client';

import { Skeleton } from '@repo/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow
} from '@repo/ui/components/table';
import { TableShellHead } from '@repo/ui/components/table-shell';
import { cn } from '@repo/ui/lib/utils';

import type {
  InvestorMetricWindow,
  InvestorPerformance
} from '@/lib/investor-analytics';
import type { InvestorRiskMetrics } from '../types/api';
import { benchmarkWindowReturnPct } from '../utils/benchmarks';
import { benchmarkLabel } from '@/lib/benchmarks';
import { formatCount } from '@/lib/format/number/count';
import { formatBasisPoints } from '@/lib/format/percent/basis-points';
import { listInWords } from '@/lib/format/list';
import { formatPercent } from '@/lib/format/percent/percent';

export interface PeriodReturnRow {
  label: string;
  /** The window this row asks for: its identity ('inception' is Since start). */
  window: InvestorMetricWindow;
  /** The served window metrics, once the row has them. */
  metrics?: InvestorRiskMetrics;
  isLoading: boolean;
  /** The row failed with nothing cached to show in its place. */
  isError: boolean;
}

interface PeriodReturnsProps {
  rows: PeriodReturnRow[];
  /** Daily closes of the compared index, matched by symbol by the view. */
  benchmark?: InvestorPerformance['benchmarks'][number];
  benchmarkLoading?: boolean;
  /** The benchmark series failed with nothing cached to show. */
  benchmarkError?: boolean;
}

/**
 * One sentence for the rows whose period is longer than the account's
 * history. /metrics clamps each window to the history there is
 * (`windowDays` = min(requested, `history.availableDays`)), so on a young
 * account those rows all run from the first deposit and read the same
 * (QA 2026-10-04: identical 1M, 3M, YTD, 1Y and Since start on a 15 day old
 * account). "Since start" is that by definition and is never named; it is
 * known by the window the row asks for, not by the served echo. Null when
 * no row is cut short.
 */
function shortHistoryNote(rows: PeriodReturnRow[]): string | null {
  let historyDays = 0;
  const cutShort: string[] = [];
  for (const row of rows) {
    const metrics = row.metrics;
    if (row.isError || !metrics || row.window === 'inception') continue;
    const available = metrics.history.availableDays;
    if (available > 0 && metrics.windowDays >= available) {
      cutShort.push(row.label);
      historyDays = Math.max(historyDays, available);
    }
  }
  if (cutShort.length === 0) return null;
  return `You have ${formatCount(historyDays)} day${historyDays === 1 ? '' : 's'} of history, so ${listInWords(cutShort)} ${cutShort.length === 1 ? 'shows' : 'each show'} your return since your first deposit.`;
}

function CellSkeleton({ className }: { className?: string }) {
  return (
    <TableCell className={cn('py-3', className)}>
      <Skeleton className="h-5 w-full" />
    </TableCell>
  );
}

/**
 * Returns by period (1M / 3M / YTD / 1Y / since start) against the selected
 * benchmark, rendered as a section inside the performance card — the chart
 * shows the shape, this table states the numbers, one card tells the story.
 * Portfolio figures are the backend's flow-adjusted window returns — never
 * recomputed from the value series, which deposits would distort. The
 * benchmark is measured between each row's own served window edges on the
 * index's daily closes.
 *
 * The view owns the queries; this only renders them. A row that failed says
 * so in its own cells instead of printing a dash that reads as "no return".
 */
export function PeriodReturns({
  rows,
  benchmark,
  benchmarkLoading = false,
  benchmarkError = false
}: PeriodReturnsProps) {
  const hasFailure = benchmarkError || rows.some((row) => row.isError);
  const historyNote = shortHistoryNote(rows);
  // The note can only be known once every row has answered. Its line is
  // held meanwhile, so the table does not jump when it appears.
  const rowsLoading = rows.some((row) => row.isLoading);

  return (
    <section
      aria-labelledby="period-returns-title"
      className="border-rowa-border-default mt-6 min-w-0 border-t pt-5 xl:mt-0 xl:border-t-0 xl:border-l xl:pt-0 xl:pl-6"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3
          id="period-returns-title"
          className="text-rowa-card text-rowa-text-primary"
        >
          Returns by period
        </h3>
        <p className="text-rowa-label-sm text-rowa-text-tertiary">
          Adjusted for deposits and withdrawals
        </p>
      </div>

      <div className="mt-1 min-w-0 overflow-x-auto [&_tr]:border-none">
        <Table aria-label="Returns by period">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableShellHead className="pl-0">Period</TableShellHead>
              <TableShellHead align="right">Your return</TableShellHead>
              <TableShellHead align="right">
                {benchmark ? benchmarkLabel(benchmark) : 'Benchmark'}
              </TableShellHead>
              <TableShellHead align="right" className="pr-0">
                Difference
              </TableShellHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              if (row.isLoading) {
                return (
                  <TableRow key={row.label} className="hover:bg-transparent">
                    <CellSkeleton className="pl-0" />
                    <CellSkeleton />
                    <CellSkeleton />
                    <CellSkeleton className="pr-0" />
                  </TableRow>
                );
              }

              if (row.isError || !row.metrics) {
                return (
                  <TableRow key={row.label} className="hover:bg-transparent">
                    <TableCell className="text-rowa-body-sm text-rowa-text-primary py-3 pl-0 font-medium">
                      {row.label}
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      className="text-rowa-body-sm text-rowa-text-tertiary pr-0 text-right"
                    >
                      Could not load
                    </TableCell>
                  </TableRow>
                );
              }

              const returnBps = row.metrics.windowReturnBps;
              const portfolioPct = returnBps != null ? returnBps / 100 : null;
              const benchmarkPct = benchmarkWindowReturnPct(benchmark?.series, {
                from: row.metrics.windowFrom,
                to: row.metrics.windowTo
              });
              const differencePp =
                portfolioPct != null && benchmarkPct != null
                  ? portfolioPct - benchmarkPct
                  : null;

              return (
                <TableRow key={row.label} className="hover:bg-transparent">
                  <TableCell className="text-rowa-body-sm text-rowa-text-primary py-3 pl-0 font-medium">
                    {row.label}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'text-rowa-body-sm text-right font-mono tabular-nums',
                      portfolioPct != null && portfolioPct < 0
                        ? 'text-rowa-status-error'
                        : 'text-rowa-text-primary'
                    )}
                  >
                    {returnBps != null
                      ? formatBasisPoints(returnBps, {
                          signed: true,
                          fractionDigits: 1
                        })
                      : '—'}
                  </TableCell>
                  {benchmarkLoading ? (
                    <>
                      <CellSkeleton />
                      <CellSkeleton className="pr-0" />
                    </>
                  ) : benchmarkError && !benchmark ? (
                    <TableCell
                      colSpan={2}
                      className="text-rowa-body-sm text-rowa-text-tertiary pr-0 text-right"
                    >
                      Could not load
                    </TableCell>
                  ) : (
                    <>
                      <TableCell className="text-rowa-body-sm text-rowa-text-secondary text-right font-mono tabular-nums">
                        {formatPercent(benchmarkPct, {
                          digits: 1,
                          sign: 'plus-minus'
                        })}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'text-rowa-body-sm pr-0 text-right font-mono tabular-nums',
                          differencePp != null && differencePp < 0
                            ? 'text-rowa-status-error'
                            : 'text-rowa-text-primary'
                        )}
                      >
                        {differencePp == null
                          ? '—'
                          : formatPercent(differencePp, {
                              digits: 1,
                              sign: 'plus-minus',
                              unit: ' pp'
                            })}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {rowsLoading ? (
        <Skeleton
          data-period-note="loading"
          aria-hidden
          className="mt-2 h-4 w-3/4"
        />
      ) : historyNote ? (
        <p className="text-rowa-label-sm text-rowa-text-tertiary mt-2">
          {historyNote}
        </p>
      ) : null}

      {hasFailure ? (
        <p className="text-rowa-label-sm text-rowa-text-tertiary mt-2">
          Some figures could not load. Use Retry portfolio data at the top of
          the page to load them again.
        </p>
      ) : null}
    </section>
  );
}
